import type { FC, ReactNode } from "react";

import { PageHeader } from "@/components/ui/page-header";
import { cn } from "@/lib/utils";

type Props = {
  title?: ReactNode;
  count?: number | string | null;
  description?: ReactNode;
  actions?: ReactNode;
  /** `default` is the 760px settings column; `wide` (1024px) is for legacy admin tables. */
  width?: "default" | "wide";
  /** Off when an outer element already scrolls (the app's main area). */
  scroll?: boolean;
  className?: string;
  children: ReactNode;
};

/**
 * Connectors, firm and account settings, the client Settings tab and forms:
 * a centered column of `SectionHeader` plus `ListCard` sections, 32px apart.
 * The page scrolls itself unless `scroll` is off.
 */
export const SettingsPage: FC<Props> = ({
  title,
  count,
  description,
  actions,
  width = "default",
  scroll = true,
  className,
  children
}) => (
  <div
    data-slot="settings-page"
    className={cn(
      "w-full bg-surface",
      scroll && "min-h-0 flex-1 overflow-y-auto"
    )}
  >
    <div
      className={cn(
        "mx-auto flex w-full flex-col gap-8 px-12 pt-10 pb-24",
        width === "wide" ? "max-w-256" : "max-w-190",
        className
      )}
    >
      {title ? (
        <PageHeader
          title={title}
          count={count}
          description={description}
          actions={actions}
        />
      ) : null}
      {children}
    </div>
  </div>
);
